/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { metricMeta } from './metrics';

/** Riepilogo ultimi 7 giorni per metrica. */
export const SummarySection = ({ loading, summary }) => {
  const entries = Object.entries(summary?.metrics || {});
  return (
    <section className="space-y-3" data-testid="iot-summary-section">
      <h2 className="font-display text-lg font-semibold">Riepilogo ultimi 7 giorni</h2>
      {loading && <Skeleton className="h-40" />}
      {!loading && entries.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Nessuna misurazione negli ultimi 7 giorni. Aggiungi manualmente un valore per iniziare.
          </CardContent>
        </Card>
      )}
      {!loading && entries.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {entries.map(([key, s]) => {
            const meta = metricMeta(key);
            const Icon = meta.Icon;
            return (
              <Card key={key} data-testid={`iot-summary-${key}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {meta.label}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-2xl font-bold font-display">{s.latest}</span>
                    <span className="text-xs text-muted-foreground">{s.unit || meta.unit}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    Media: {s.avg} · Min: {s.min} · Max: {s.max} · {s.count} misur.
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
};

/** Elenco ultime misurazioni. */
export const MeasurementsSection = ({ loading, measurements }) => (
  <section className="space-y-3" data-testid="iot-measurements-section">
    <h2 className="font-display text-lg font-semibold">Ultime misurazioni</h2>
    {loading && <Skeleton className="h-24" />}
    {!loading && measurements.length === 0 && (
      <Card><CardContent className="p-4 text-sm text-muted-foreground">Nessuna misurazione.</CardContent></Card>
    )}
    {!loading && measurements.length > 0 && (
      <Card>
        <CardContent className="p-0">
          <ul className="divide-y divide-border" data-testid="iot-measurements-list">
            {measurements.map((m) => {
              const meta = metricMeta(m.metric);
              const Icon = meta.Icon;
              return (
                <li key={m.id} className="flex items-center gap-3 px-4 py-3" data-testid={`iot-measurement-${m.id}`}>
                  <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center"><Icon className="h-4 w-4 text-muted-foreground" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{meta.label}</div>
                    <div className="text-[11px] text-muted-foreground">{new Date(m.measured_at).toLocaleString('it')} · {m.provider}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-base font-bold">{m.value}</div>
                    <div className="text-[10px] uppercase text-muted-foreground">{m.unit || meta.unit}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    )}
  </section>
);
